import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { trace, SpanStatusCode } from "@opentelemetry/api";
import { getUserId } from "./init";

const tracer = trace.getTracer("wine-web");

function getRoutePath(pathname: string) {
  return pathname.split(/[?#]/, 1)[0] || "/";
}

function getRouteFailureType(error: unknown) {
  if (error instanceof TypeError) return "TypeError";
  if (error instanceof Error) return "Error";
  return `non-Error:${typeof error}`;
}

export function recordRouteLoadFailure(pathname: string, error: unknown) {
  const span = tracer.startSpan("route.load_failure");
  span.setAttribute("route.path", getRoutePath(pathname));
  span.setAttribute("error.type", getRouteFailureType(error));
  span.setStatus({ code: SpanStatusCode.ERROR });
  span.end();
}

export function useRouteTracking() {
  const location = useLocation();
  const prevPathRef = useRef<string | null>(null);
  const pageEntryRef = useRef<number>(performance.now());

  useEffect(() => {
    const fromPath = prevPathRef.current;
    const toPath = location.pathname;

    // Record dwell time for the previous page
    if (fromPath !== null) {
      const dwellMs = performance.now() - pageEntryRef.current;
      const dwellSpan = tracer.startSpan("page.dwell");
      dwellSpan.setAttribute("route.path", fromPath);
      dwellSpan.setAttribute("dwell.ms", Math.round(dwellMs));
      const userId = getUserId();
      if (userId) dwellSpan.setAttribute("user.id", userId);
      dwellSpan.setStatus({ code: SpanStatusCode.OK });
      dwellSpan.end();
    }

    // Record page view for the new page
    const viewSpan = tracer.startSpan("page_view");
    viewSpan.setAttribute("route.path", toPath);
    if (fromPath !== null) {
      viewSpan.setAttribute("route.from", fromPath);
    }
    const userId = getUserId();
    if (userId) viewSpan.setAttribute("user.id", userId);
    viewSpan.setStatus({ code: SpanStatusCode.OK });
    viewSpan.end();

    prevPathRef.current = toPath;
    pageEntryRef.current = performance.now();
  }, [location.pathname]);

  // Track dwell on tab hide / page unload
  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState === "hidden" && prevPathRef.current) {
        const dwellMs = performance.now() - pageEntryRef.current;
        const span = tracer.startSpan("page.dwell");
        span.setAttribute("route.path", prevPathRef.current);
        span.setAttribute("dwell.ms", Math.round(dwellMs));
        span.setAttribute("dwell.reason", "hidden");
        const userId = getUserId();
        if (userId) span.setAttribute("user.id", userId);
        span.setStatus({ code: SpanStatusCode.OK });
        span.end();
      }
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);
}
